/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useRef } from 'react';
import { FileInputProps } from '../../../types/InputComponentProps';
import { InputContainer } from './FormInputContainer';
import { HStack } from 'chakra-v3';
import { Button, Text } from '../../../../../components/Index';

export function FileInput(props: FileInputProps) {
  const {
    id,
    description,
    label,
    value,
    error,
    isInvalid,
    isDisabled,
    acceptedTypes,
    isRequired,
    onChange,
    size: _size,
    setError,
    ...rest
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const handleBrowse = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return onChange?.(e);
    }

    if (acceptedTypes) {
      const isMimeValid = acceptedTypes.includes(file.type);

      if (!isMimeValid) {
        e.target.value = '';
        setError?.(id, `Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`);
        return;
      }
    }

    onChange?.(e);
  };

  return (
    <InputContainer
      id={id}
      description={description}
      label={label}
      error={error}
      isInvalid={isInvalid}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      <input
        id={id}
        type="file"
        ref={inputRef}
        hidden
        accept={acceptedTypes?.join(',')}
        {...rest}
        onChange={handleChange}
      />
      <HStack gap={3} alignItems="center">
        <Button variant="secondary" disabled={isDisabled} onClick={handleBrowse} type="button">
          Select File
        </Button>
        {value && value instanceof File && (
          <Text variant="body" color="text.primary" opacity={0.8}>
            {value.name}
          </Text>
        )}
      </HStack>
    </InputContainer>
  );
}
